import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { photosAPI } from "@/lib/api/photos";

export const photoKeys = {
  byId: (consultantId: number) => ["consultant-photo", consultantId] as const,
};

/**
 * The consultant's profile photo as a component-owned object URL, or null
 * when none exists (#776).
 *
 * The query caches the **Blob** — never the object URL — so cache
 * invalidation/eviction can't leak URLs; each observer mints its own URL in
 * an effect and revokes it on change/unmount (object URLs are cheap
 * references to the shared in-memory Blob). `staleTime: Infinity` because a
 * photo only changes through our own mutations, which invalidate the key;
 * `gcTime` stays at the app default (10 min) so unobserved blobs are evicted
 * and photos uploaded from another session eventually appear. A 404 (no
 * photo) caches as null — an expected state, not an error, hence
 * `retry: false`.
 */
export const useConsultantPhoto = (consultantId?: number | null) => {
  const query = useQuery({
    queryKey: photoKeys.byId(consultantId ?? -1),
    queryFn: () => photosAPI.getConsultantPhoto(consultantId as number),
    enabled: consultantId !== null && consultantId !== undefined,
    staleTime: Infinity,
    retry: false,
  });

  const blob = query.data ?? null;
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setPhotoUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  return { photoUrl, loading: query.isLoading };
};

/** Upload/remove the signed-in consultant's own photo. */
export const useMyPhotoMutations = (consultantId?: number | null) => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    if (consultantId !== null && consultantId !== undefined) {
      void queryClient.invalidateQueries({ queryKey: photoKeys.byId(consultantId) });
    }
  };

  const upload = useMutation({
    mutationFn: (file: File) => photosAPI.uploadMyPhoto(file),
    onSuccess: () => {
      invalidate();
      toast.success("Profile photo updated");
    },
  });

  const remove = useMutation({
    mutationFn: () => photosAPI.deleteMyPhoto(),
    onSuccess: () => {
      invalidate();
      toast.success("Profile photo removed");
    },
  });

  return {
    uploadPhoto: upload.mutateAsync,
    removePhoto: remove.mutateAsync,
    busy: upload.isPending || remove.isPending,
  };
};

/** Admin upload/remove of a consultant's photo. */
export const useAdminPhotoMutations = (consultantId: number) => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: photoKeys.byId(consultantId) });

  const upload = useMutation({
    mutationFn: (file: File) => photosAPI.adminUploadPhoto(consultantId, file),
    onSuccess: () => {
      invalidate();
      toast.success("Photo updated");
    },
  });

  const remove = useMutation({
    mutationFn: () => photosAPI.adminDeletePhoto(consultantId),
    onSuccess: () => {
      invalidate();
      toast.success("Photo removed");
    },
  });

  return {
    uploadPhoto: upload.mutateAsync,
    removePhoto: remove.mutateAsync,
    busy: upload.isPending || remove.isPending,
  };
};
